import { useOnValueChange, useQueryParams } from '@calendly/hooks'
import { createContext, useContext } from 'react'
import { useHistory } from 'react-router-dom'

import { useGetOrganizationQuery, useGetPolicyQuery } from 'common'

import { selectProfileById, useFetchEventTypesQueryState, useSchedulingFetchProfilesQuery } from './api'

const EventTypeListContext = createContext({})
const ProfileContext = createContext({})
const EventTypeContext = createContext({})

export const { Provider: EventTypeListContextProvider } = EventTypeListContext
export const { Provider: ProfileContextProvider } = ProfileContext
export const { Provider: EventTypeContextProvider } = EventTypeContext

export const useEventTypeListData = () => useContext(EventTypeListContext)

export const useProfilesData = () => {
  const history = useHistory()
  const { scopeType, scopeId } = useEventTypeListData()
  // Remove profile query param to prevent refetching data when interacting with the users profile sidebar
  const { profile: _profile, pane: _pane, paneState: _paneState, ...queryParams } = useQueryParams()
  const result = useSchedulingFetchProfilesQuery({ scopeType, scopeId, queryParams })

  useOnValueChange(result.error, () => {
    if (!result.error) return

    if (result.error.type === 'policy' && scopeType !== 'my_calendly') {
      history.push('/app/scheduling/meeting_types/user/me')
    }
  })

  return result
}

export const useCurrentProfile = () => {
  const { id } = useContext(ProfileContext)
  const { data } = useProfilesData()

  return selectProfileById(data.results, id)
}

export const useCurrentPagination = () => {
  const { pagination } = useEventTypeListData()

  return pagination || { current_page: 1 }
}

export const useHasAccessToTeams = () => {
  const { data: organization } = useGetOrganizationQuery()
  const { data: policy } = useGetPolicyQuery()

  return organization.kind === 'multiple' && policy.canListTeams
}

export const useIsOnLastPage = () => {
  const { data, isFetching } = useProfilesData()

  return !isFetching
    && data
    && (!data.pagination || data.pagination.current_page >= data.pagination.total_pages)
}

export const useCurrentEventType = () => {
  const { scopeType } = useEventTypeListData()
  const { uuid, isSortable } = useContext(EventTypeContext)
  const { current_page: profilePage } = useCurrentPagination()
  const { data: setOfEventTypes } = useFetchEventTypesQueryState({ profilePage, scopeType })

  const data = setOfEventTypes?.[uuid]

  return { data, isSortable }
}
